import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useHistory } from 'react-router-dom';
import get from 'lodash.get';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import {
  Button,
  ConfirmationPopup,
  ContentLoader,
  IconCheckmark,
  IconDisabled,
  List,
  Notification,
  StatusCard,
} from 'shared/components';
import {
  displayValue,
  formatDate,
  hasPermission,
  getCurrentUserPermissions,
} from 'shared/helpers';
import { errorMsg } from 'shared/constants';
import {
  disableLicenses,
  getOrderLicenses,
} from 'orders/OrderContainer/actions';
import { getLicenseProductLabel } from './helpers';

const PAGE_SIZE = 20;

const LicensesList = ({ order, refetchOrder }) => {
  const { t } = useTranslation();
  const permissionsList = useSelector((state) =>
    getCurrentUserPermissions(state),
  );
  const history = useHistory();
  const orderId = get(order, 'id');
  const [orderLicenses, setOrderLicenses] = useState([]);
  const [isLicensesLoading, setLicensesLoading] = useState(false);
  const [isLicensesFetchFailed, setLicensesFetchFailed] = useState(false);
  const [count, setCount] = useState(0);
  const [page, setPage] = useState(0);
  const [reloadToken, setReloadToken] = useState(0);
  const canIssueLicenses = hasPermission(permissionsList, 'issue_license');
  const isBundle = get(order, 'type') === 'bundle';
  const licensesData = orderLicenses;
  const bundleLicenses = licensesData.filter((l) => l.is_bundle);
  const displayedLicenses = isBundle ? bundleLicenses : licensesData;
  const totalCount = count;
  const pages = Math.ceil(totalCount / PAGE_SIZE);

  const [disablingLicenses, setDisablingLicenses] = useState(false);
  const [disableBulkLicensePopupDisplayed, setDisableBulkLicensePopupDisplay] =
    useState(false);

  useEffect(() => {
    setPage(0);
  }, [orderId]);

  useEffect(() => {
    if (!orderId) {
      return;
    }

    setLicensesLoading(true);
    setLicensesFetchFailed(false);

    getOrderLicenses(orderId, {
      limit: PAGE_SIZE,
      offset: page * PAGE_SIZE,
    })
      .then((res) => {
        const payload = get(res, 'data');
        const results = Array.isArray(payload)
          ? payload
          : get(payload, 'results', []);
        const normalizedResults = results.map((license) => ({
          ...license,
          product: getLicenseProductLabel(license),
        }));
        const fetchedCount = Array.isArray(payload)
          ? normalizedResults.length
          : get(payload, 'count', normalizedResults.length);

        setOrderLicenses(normalizedResults);
        setCount(fetchedCount);
        setLicensesLoading(false);
      })
      .catch(() => {
        setOrderLicenses([]);
        setCount(0);
        setLicensesFetchFailed(true);
        setLicensesLoading(false);
      });
  }, [orderId, page, reloadToken]);

  const redirectToLicensePage = (rowData) => {
    const licenseId = get(rowData, 'original.id');
    history.push(`/licenses/${licenseId}`);
  };

  const fetchAllOrderLicenseIds = async () => {
    if (!orderId) {
      return [];
    }

    const allLicenseIds = [];
    let offset = 0;
    let fullCount = null;

    while (fullCount === null || offset < fullCount) {
      const response = await getOrderLicenses(orderId, {
        limit: PAGE_SIZE,
        offset,
      });
      const payload = get(response, 'data');
      const results = Array.isArray(payload)
        ? payload
        : get(payload, 'results', []);

      if (!results.length) {
        break;
      }

      results.forEach((license) => {
        const licenseId = get(license, 'id');
        if (licenseId) {
          allLicenseIds.push(licenseId);
        }
      });

      if (Array.isArray(payload)) {
        break;
      }

      fullCount = get(payload, 'count', allLicenseIds.length);
      offset += PAGE_SIZE;
    }

    return Array.from(new Set(allLicenseIds));
  };

  const handleLicensesDisable = () => {
    setDisablingLicenses(true);

    fetchAllOrderLicenseIds()
      .then((licenseIds) => {
        if (!licenseIds.length) {
          throw new Error('No licenses found');
        }

        return disableLicenses(licenseIds);
      })
      .then(() => {
        refetchOrder();
        setReloadToken((token) => token + 1);
        setDisablingLicenses(false);
        setDisableBulkLicensePopupDisplay(false);
        Notification('success', t('Licenses succesfully disabled'));
      })
      .catch(() => {
        setDisablingLicenses(false);
        Notification(
          'error',
          t('Error occured'),
          t('Your changes were not saved'),
        );
      });
  };

  const handleBulkLicenseDisableClick = () => {
    const permission = hasPermission(permissionsList, 'reset_license');
    if (!permission) {
      Notification(
        'error',
        t('Missing permission'),
        t('You do not have permission to perform this action.'),
      );
      return false;
    }
    setDisableBulkLicensePopupDisplay(true);
    return true;
  };

  const handleNewOrder = () => {
    if (!canIssueLicenses) {
      Notification(
        'error',
        t('Missing permission'),
        t('You do not have permission to perform this action.'),
      );
      return false;
    }
    history.push(`/create-order/${get(order, 'id')}`);
    return true;
  };

  if (isLicensesLoading && !displayedLicenses.length) {
    return <ContentLoader text={t('Getting order licenses')} />;
  }

  if (isLicensesFetchFailed) {
    return (
      <div className="LicensesList-error">
        <p>{t('Unable to load order licenses')}</p>
        <Button
          theme="info"
          size="sm"
          onClick={() => setReloadToken((token) => token + 1)}
        >
          {t('Retry')}
        </Button>
      </div>
    );
  }

  return (
    <div className="LicensesList licenses-table">
      <div className="list-header">
        <div>
          {canIssueLicenses && (
            <Button onClick={handleNewOrder} theme="info" size="sm">
              {t('Issue new Licenses')}
            </Button>
          )}
        </div>
        <div>
          <Button
            theme="error"
            size="sm"
            onClick={handleBulkLicenseDisableClick}
          >
            {t('Disable all licenses')}
          </Button>
        </div>
      </div>
      <List
        clickable
        columns={[
          {
            id: 'license_key',
            Header: `${t('License key')}/${t('User')}`,
            width: 250,
            sortable: false,
            Cell: (rowData) => {
              const licenseUsers = get(rowData, 'original.license_users', []);
              const usersNum = licenseUsers.length;
              const licenseKey = get(rowData, 'original.license_key');
              if (usersNum > 0) {
                const firstUser = get(licenseUsers, '[0].true_email');
                const isSingleUser = usersNum === 1;
                if (isSingleUser) {
                  return `${firstUser}`;
                }
                return `${firstUser} (+${usersNum - 1})`;
              }
              if (licenseKey) {
                return displayValue(licenseKey);
              }
              return t(errorMsg.notAssigned);
            },
          },
          {
            accessor: 'license_type',
            Header: t('License Type'),
            Cell: (rowData) => displayValue(t(rowData.value)),
            show: !isBundle,
          },
          {
            accessor: 'validity_period',
            Header: t('Expiration Date'),
            className: 'text-center',
            headerClassName: 'text-center',
            Cell: (rowData) => displayValue(rowData.value),
            show: !isBundle,
          },
          {
            accessor: 'product',
            Header: t('Product'),
            width: 150,
          },
          {
            accessor: 'created_at',
            Header: t('Created on'),
            Cell: (rowData) => formatDate(rowData.value),
          },
          {
            accessor: 'status',
            className: 'text-center',
            Header: t('Status'),
            headerClassName: 'text-center',
            Cell: (cellInfo) => (
              <StatusCard
                status={cellInfo.value === 'Active' ? 'success' : 'error'}
                text={t(cellInfo.value)}
              />
            ),
            show: !isBundle,
          },
          {
            accessor: 'is_trial',
            Header: t('Is trial'),
            className: 'text-center',
            headerClassName: 'text-center',
            Cell: (rowData) =>
              rowData.value ? (
                <IconCheckmark color="#10ac84" height="14px" />
              ) : (
                <IconDisabled color="#aaa" height="14px" />
              ),
            show: !isBundle,
          },
          {
            accessor: 'is_bundle',
            Header: t('Is bundle'),
            className: 'text-center',
            headerClassName: 'text-center',
            Cell: (cellData) =>
              cellData.value ? (
                <IconCheckmark color="#10ac84" height="14px" />
              ) : (
                <IconDisabled color="#aaa" height="14px" />
              ),
            width: 100,
            show: isBundle,
          },
        ]}
        data={displayedLicenses}
        handleClick={(rowData) => redirectToLicensePage(rowData)}
        manual
        page={page}
        pages={pages}
        minRows={displayedLicenses.length || 1}
        showPagination={pages > 1}
        loading={isLicensesLoading}
        onPageChange={(newPage) => setPage(newPage)}
        defaultPageSize={PAGE_SIZE}
        sortable={false}
      />
      {disableBulkLicensePopupDisplayed && (
        <ConfirmationPopup
          closeCb={() => setDisableBulkLicensePopupDisplay(false)}
          confirmCb={handleLicensesDisable}
          confirmText={t('Disable')}
          disabled={disablingLicenses}
          theme="error"
          title={t(
            'Are you sure you want to disable all licenses from this order?',
          )}
        />
      )}
    </div>
  );
};

LicensesList.propTypes = {
  order: PropTypes.object.isRequired,
  refetchOrder: PropTypes.func.isRequired,
};

export default LicensesList;
